import {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {isEmpty} from "es-toolkit/compat";

import {
  createBespokeTest,
  loadClientSecret,
  updateBaseTest,
  updateCardDetails,
  updateCollectionMethod,
  updateNextDispatch,
  updateReactivateSubscription,
} from "@/features/subscriptions";
import {config} from "@/config";
import {COLLECTION_METHOD} from "@/constants/collectionMethod";
import {selectUserDetails} from "@/features/user/userSelectors";
import {selectSubscriptionsData} from "@/features/subscriptions/subscriptionsSelectors";

const FORTH_WEBSITE_ID = config.websiteId;

export const useSubscriptions = (options = {}) => {
  const {
    subscription = {},
    amount = 0,
    newDispatchDate = "",
    newFrequency = "",
    phlebotomyIdSelected = 0,
    deliveryFee = 0,
  } = options;

  const subscriptionState = useSelector(selectSubscriptionsData);
  const userDetails = useSelector(selectUserDetails);
  const [clientSecret, setClientSecret] = useState("");
  const [stripeId, setStripeId] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    setIsLoading(true);
    loadStripeDetails();
  }, []);

  const loadStripeDetails = () => {
    dispatch(loadClientSecret())
      .then(({payload: response}) => {
        const {client_secret, id, currency, amount} = response;
        setClientSecret(client_secret);
        setStripeId(id);
      })
      .finally(() => setIsLoading(false));
  };

  const reactivateSubscription = (params = {}) => {
    const {stripeToken = ""} = params;
    const paypalSubStatus = 3;

    const payload = {
      stripeToken,
      stripeCustomerId: userDetails.customerStripeId || subscription.stripeCustomerId,
      subscriptionId: subscription.subscriptionId,
      billingCycle: subscription.billingFrequency,
      startDate: subscription.nextDispatch,
      price: amount,
      collectionMethod: subscription.phlebotomyOptionId,
      subscriptionType: subscription.type,
      subscriptionStatus: subscription.type === "paypal" ? paypalSubStatus : "",
    };

    return dispatch(updateReactivateSubscription(payload));
  };

  const changeCard = (params = {}) => {
    const {stripeToken = null} = params;

    const payload = {
      stripeCustomerId: userDetails.customerStripeId || subscription.stripeCustomerId,
      stripeProfileId: subscription.stripeProfileId,
      stripeToken,
      subscriptionId: subscription.subscriptionId,
      subscriptionType: subscription.type,
    };

    return dispatch(updateCardDetails(payload));
  };

  const changeNextDispatch = (params = {}) => {
    const {stripeToken = null} = params;

    const payload = {
      stripeCustomerId: userDetails.customerStripeId || subscription.stripeCustomerId,
      stripeToken,
      subscriptionId: subscription.subscriptionId,
      subscriptionType: subscription.type,
      subscriptionStatus: "newDateFrequency",
      newDispatchDate,
      newFrequency,
    };

    return dispatch(updateNextDispatch(payload));
  };

  const changeCollectionMethod = (params = {}) => {
    const {stripeToken = null} = params;

    const subscriptionPhlebotomy = subscription.phlebotomy;
    const testType = subscription.testType;
    const postKit = subscription.postPKit;
    const phlebotomyKitCode = subscription.phlebotomyKitCode;
    const kitStatus = subscription.kitStatus;

    const subscriptionStatus = {
      1: "fingerPrickKit",
      2: "phlebotomyAppointment",
      3: "phlebotomyKit",
      5: "phlebotomyClinic",
    };

    // ? FROM: MySubscriptions.js
    if (phlebotomyKitCode !== "KT257") {
      if (subscriptionPhlebotomy == 0 && phlebotomyKitCode !== "") {
        subscriptionStatus[3] = "phlebotomyKitF";
      }

      if (subscriptionPhlebotomy == 1 && postKit == 0) {
        subscriptionStatus[3] = "phlebotomyKit";
        if (testType != 2) {
          subscriptionStatus[1] = "fingerPrickKit";
        }
      }

      if (subscriptionPhlebotomy == 0 && postKit == 1) {
        if (testType != 2) {
          subscriptionStatus[1] = "fingerPrickKitF";
        }
      }
    }

    const payload = {
      stripeCustomerId: userDetails.customerStripeId || subscription.stripeCustomerId,
      stripeToken,
      subscriptionId: subscription.subscriptionId,
      subscriptionType: subscription.type,
      subscriptionStatus: subscriptionStatus[phlebotomyIdSelected],
    };

    return dispatch(updateCollectionMethod(payload));
  };

  const changeBaseTest = (params = {}) => {
    const {stripeToken = null, amount} = params;

    const collectionMethodOptions = {
      [COLLECTION_METHOD.FingerPrick.id]: "Finger prick kit",
      [COLLECTION_METHOD.HomePhlebotomy.id]: "Phlebotomy kit with home appointment",
      [COLLECTION_METHOD.PhlebotomyKit.id]: "Phlebotomy kit",
      [COLLECTION_METHOD.PartnerClinic.id]: "Phlebotomy with clinic appointment",
    };

    const payload = {
      stripeToken,
      stripeCustomerId: userDetails.customerStripeId || subscription.stripeCustomerId,
      subscriptionId: subscription.subscriptionId,
      subquentialAmounts: amount,
      testId: subscription.testId,
      collectionMethod: collectionMethodOptions[subscription.phlebotomyOption],
      websiteId: FORTH_WEBSITE_ID,
      billingFrequency: subscription.billingFrequency,
      subscriptionType: subscription.type,
      deliveryFee,
    };

    return dispatch(updateBaseTest(payload));
  };

  const changeMarkers = (params = {}) => {
    const {stripeToken = null, amount = 0, plusTestSelected = []} = params;

    const collectionMethodOptions = {
      [COLLECTION_METHOD.FingerPrick.id]: "Finger prick kit",
      [COLLECTION_METHOD.HomePhlebotomy.id]: "Phlebotomy kit with home appointment",
      [COLLECTION_METHOD.PhlebotomyKit.id]: "Phlebotomy kit",
      [COLLECTION_METHOD.PartnerClinic.id]: "Phlebotomy with clinic appointment",
    };

    const newSubscriptionPayload = {
      isBespokeWithNoBase: subscription.isBespokeWithNoBase,
      parentTestId: subscription.testId,
      selectedTests: plusTestSelected,
      websiteId: FORTH_WEBSITE_ID,
    };

    return createNewSubscriptionTest(newSubscriptionPayload).then(response => {
      const payload = {
        stripeToken,
        stripeCustomerId: userDetails.customerStripeId || subscription.stripeCustomerId,
        subscriptionId: subscription.subscriptionId,
        subquentialAmounts: amount,
        testId: response.testId,
        collectionMethod: collectionMethodOptions[subscription.phlebotomyOption],
        websiteId: FORTH_WEBSITE_ID,
        billingFrequency: subscription.billingFrequency,
        subscriptionType: subscription.type,
        deliveryFee,
      };

      return dispatch(updateBaseTest(payload));
    });
  };

  const createCustomTest = (params = {}) => {
    const {stripeToken = null, amount = 0} = params;

    const collectionMethodOptions = {
      [COLLECTION_METHOD.FingerPrick.id]: "Finger prick kit",
      [COLLECTION_METHOD.HomePhlebotomy.id]: "Phlebotomy kit with home appointment",
      [COLLECTION_METHOD.PhlebotomyKit.id]: "Phlebotomy kit",
      [COLLECTION_METHOD.PartnerClinic.id]: "Phlebotomy with clinic appointment",
    };

    const payload = {
      stripeToken,
      stripeCustomerId: userDetails.customerStripeId || subscription.stripeCustomerId,
      subscriptionId: subscription.subscriptionId,
      subquentialAmounts: amount,
      testId: subscription.testId,
      collectionMethod: collectionMethodOptions[subscription.phlebotomyOption],
      websiteId: FORTH_WEBSITE_ID,
      billingFrequency: subscription.billingFrequency,
      subscriptionType: subscription.type,
      deliveryFee,
    };

    return dispatch(updateBaseTest(payload));
  };

  const createNewSubscriptionTest = payload => {
    return dispatch(createBespokeTest(payload)).then(({payload: response}) => {
      if (!response.error) {
        return response;
      }

      return {error: true, errorMsg: response.errorMsg};
    });
  };

  const getNewSubscriptionState = (params = {}) => {
    const {subscriptionUpdated} = params;
    if (isEmpty(subscriptionUpdated)) return;

    const {subscriptionId, type} = subscriptionUpdated || {};

    return {
      ...subscriptionState,
      [type]: {
        ...subscriptionState[type],
        [subscriptionId]: {
          ...subscriptionState[type][subscriptionId],
          ...subscriptionUpdated,
        },
      },
    };
  };

  return {
    clientSecret,
    stripeId,
    reactivateSubscription,
    changeCard,
    changeBaseTest,
    changeMarkers,
    createCustomTest,
    changeNextDispatch,
    changeCollectionMethod,
    getNewSubscriptionState,
    loadStripeDetails,
    isLoading,
  };
};
